import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_7b5007eb-b971-4941-9399-b2d87f9cbac2/artifacts/1lsetayu_barmann%20logo.png";
const LOGIN_BG =
  "https://images.unsplash.com/photo-1650731900879-b5f25088ff31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ3JlZW4lMjBsZWFmJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzY2MzgzNTV8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [mode]);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      toast.success(mode === "login" ? "Signed in" : "Account created");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0a0f0d]">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-10" data-testid="login-panel">
        <div className="max-w-md w-full mx-auto lg:mx-0">
          <div className="flex items-center gap-3 mb-12">
            <img src={LOGO_URL} alt="Barmann Lawn Service" className="w-14 h-14 object-contain" />
            <div>
              <div className="font-display font-black text-xl leading-tight tracking-tight">
                BARMANN
              </div>
              <div className="overline text-[10px]">Lawn Service · Ops Console</div>
            </div>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-none mb-3">
            {mode === "login" ? "Welcome back." : "Start your crew."}
          </h1>
          <p className="text-[#b0c0b6] text-base mb-10 max-w-sm">
            {mode === "login"
              ? "Sign in to manage today's route, customers, and jobs."
              : "Create an operator account to run your schedule and invoicing."}
          </p>

          <button
            type="button"
            onClick={googleSignIn}
            className="bm-btn bm-btn-google w-full mb-4"
            data-testid="google-signin-button"
          >
            <svg className="w-4 h-4" viewBox="0 0 48 48">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.3-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.6 19 12.5 24 12.5c3 0 5.8 1.1 7.9 3l5.7-5.7C33.9 6.5 29.2 4.5 24 4.5 16.2 4.5 9.5 9 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 43.5c5.1 0 9.7-1.9 13.2-5.1l-6.1-5c-2 1.4-4.4 2.2-7.1 2.2-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.4 39 16.1 43.5 24 43.5z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.2 5.5l6.1 5c-.4.4 6.3-4.6 6.3-14.5 0-1.2-.1-2.3-.3-3.5z"/>
            </svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[#233129]" />
            <span className="overline text-[10px]">or with email</span>
            <div className="flex-1 h-px bg-[#233129]" />
          </div>

          <form onSubmit={onSubmit} className="space-y-3" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="overline block mb-2">Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  required
                  className="bm-input"
                  placeholder="Your name"
                  data-testid="register-name-input"
                />
              </div>
            )}
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                required
                className="bm-input"
                placeholder="you@barmannlawn.com"
                data-testid="auth-email-input"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                name="password"
                type="password"
                value={form.password}
                onChange={onChange}
                required
                minLength={6}
                className="bm-input"
                placeholder="••••••••"
                data-testid="auth-password-input"
              />
            </div>

            {error && (
              <div
                className="text-sm text-[#e56b42] border border-[#3a2420] bg-[#1a110f] px-3 py-2 rounded-sm"
                data-testid="auth-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="bm-btn bm-btn-primary w-full mt-2"
              data-testid="auth-submit-button"
            >
              {submitting ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-[#8ba394]">
            {mode === "login" ? (
              <>
                New operator?{" "}
                <button
                  className="text-[#eae6db] underline underline-offset-4 decoration-[#c83e2d]"
                  onClick={() => setMode("register")}
                  data-testid="switch-to-register"
                >
                  Create an account
                </button>
              </>
            ) : (
              <>
                Already have an account?{" "}
                <button
                  className="text-[#eae6db] underline underline-offset-4 decoration-[#c83e2d]"
                  onClick={() => setMode("login")}
                  data-testid="switch-to-login"
                >
                  Sign in
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right: hero */}
      <div
        className="hidden lg:flex relative items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(10,15,13,0.82), rgba(10,15,13,0.82)), url(${LOGIN_BG})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="relative text-center px-10 max-w-md">
          <img src={LOGO_URL} alt="Barmann" className="w-48 mx-auto mb-10 drop-shadow-xl" />
          <div className="overline text-[#e56b42] mb-3">Firefighter-owned · Loganville, GA</div>
          <h2 className="font-display text-3xl font-black tracking-tight leading-tight mb-4">
            Ops Console for the Barmann crew.
          </h2>
          <p className="text-[#b0c0b6] text-sm leading-relaxed">
            Today's route, customer contacts, recurring jobs and invoices — all in one rugged
            command center.
          </p>
        </div>
      </div>
    </div>
  );
}
